// ============================================================
// Auth storage adapter for Supabase — IndexedDB + localStorage
// ============================================================
// On iOS "Add to Home Screen", the app runs in a separate context where
// localStorage is often cleared when the app is closed. IndexedDB tends
// to persist longer. We use IndexedDB and keep localStorage in sync so
// sessions survive. We read localStorage first when present so session
// restore is instant; we also persist to IndexedDB for next launch.
// ============================================================

const IDB_NAME = "supabase-auth";
const IDB_STORE = "auth";

let dbInstance: IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = indexedDB.open(IDB_NAME, 1);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      dbInstance = req.result;
      dbInstance.onclose = () => { dbInstance = null; };
      resolve(dbInstance);
    };
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
  });
}

function getFromIDB(key: string): Promise<string | null> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readonly");
        const store = tx.objectStore(IDB_STORE);
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => reject(req.error);
      })
  );
}

function setInIDB(key: string, value: string): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readwrite");
        const store = tx.objectStore(IDB_STORE);
        store.put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      })
  );
}

function removeFromIDB(key: string): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readwrite");
        const store = tx.objectStore(IDB_STORE);
        store.delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      })
  );
}

/**
 * Storage adapter for Supabase Auth. Uses both localStorage and IndexedDB
 * so sessions persist when the app is reopened from the home screen (iOS).
 * Reads localStorage first when present for instant restore.
 */
export function createAuthStorage(): {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
} {
  const hasLocalStorage =
    typeof window !== "undefined" &&
    typeof window.localStorage !== "undefined";

  return {
    async getItem(key: string): Promise<string | null> {
      // Read localStorage first so we return immediately when session is there
      if (hasLocalStorage) {
        try {
          const local = window.localStorage.getItem(key);
          if (local != null && local !== "") {
            setInIDB(key, local).catch(() => {}); // keep IDB in sync
            return local;
          }
        } catch {
          // ignore
        }
      }
      try {
        const idb = await getFromIDB(key);
        if (idb != null && idb !== "") return idb;
      } catch {
        // IndexedDB unavailable or failed
      }
      return null;
    },

    async setItem(key: string, value: string): Promise<void> {
      if (hasLocalStorage) {
        try {
          window.localStorage.setItem(key, value);
        } catch {
          // ignore
        }
      }
      try {
        await setInIDB(key, value);
      } catch {
        // IndexedDB failed; localStorage may still have it
      }
    },

    async removeItem(key: string): Promise<void> {
      if (hasLocalStorage) {
        try {
          window.localStorage.removeItem(key);
        } catch {
          // ignore
        }
      }
      try {
        await removeFromIDB(key);
      } catch {
        // ignore
      }
    },
  };
}
